import { useState } from 'react'
import { Avatar, Button, Chip, PriorityChip, ChatContainer, ChatBubble, Alert, Textarea } from '@/components/ui'
import { Phone, CheckCircle2, ArrowUpCircle, MoreHorizontal, Send, Paperclip, Zap, DollarSign, Search, FileText, History, Bot, UserCircle2 } from 'lucide-react'
import { fmtMoney } from '@/lib/utils'
import type { Prioridad } from '@/mock'
import { cn } from '@/lib/utils'

interface ChatAsignado {
  id: string
  cliente: string
  avatarColor: 'rose' | 'slate' | 'amber'
  prioridad: Prioridad
  tomado: string
  wa: string
  mensajePreview: string
  cedula: string
  plaza: string
  saldoVencido: number
  diasMora: number
  idCredito: number
  compromisosIncumplidos: number
}

const chats: ChatAsignado[] = [
  { id: 'ch1', cliente: 'Pablo Mendoza', avatarColor: 'rose', prioridad: 'P1', tomado: 'hace 2 min', wa: '+593 99 XXX 5555', mensajePreview: 'Si no me solucionan esto hoy...', cedula: '0908112233', plaza: 'Guayaquil', saldoVencido: 340.75, diasMora: 45, idCredito: 2050124, compromisosIncumplidos: 2 },
  { id: 'ch2', cliente: 'Lucía Benítez', avatarColor: 'slate', prioridad: 'P2', tomado: 'hace 12 min', wa: '+593 99 XXX 1122', mensajePreview: 'Negociando plan de pago...', cedula: '0913445566', plaza: 'Quito', saldoVencido: 512, diasMora: 28, idCredito: 2041122, compromisosIncumplidos: 0 },
  { id: 'ch3', cliente: 'Ana Paola Vera', avatarColor: 'slate', prioridad: 'P3', tomado: 'hace 25 min', wa: '+593 99 XXX 2211', mensajePreview: 'Problema con comprobante', cedula: '0914778899', plaza: 'Guayaquil', saldoVencido: 178.5, diasMora: 20, idCredito: 2088877, compromisosIncumplidos: 0 },
]

const plantillas = [
  'Disculpas por el inconveniente',
  'Verificar pago en banco',
  'Plan de pago 3 cuotas',
  'Llamar en 30 min',
]

export function MiMesa() {
  const [selId, setSelId] = useState(chats[0].id)
  const [draft, setDraft] = useState('')
  const [nota, setNota] = useState('')

  const sel = chats.find(c => c.id === selId)!

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden h-[760px] grid grid-cols-[260px_1fr_300px]">
      {/* COL 1 — MIS CHATS */}
      <aside className="border-r border-slate-200 flex flex-col">
        <header className="p-3 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-navy-700 text-sm">Mis chats ({chats.length})</h3>
            <Chip variant="amber" className="text-[10px]">2/5 slots</Chip>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">Hoy: 8 resueltos · 42 min prom</p>
        </header>
        <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {chats.map(c => (
            <li
              key={c.id}
              onClick={() => setSelId(c.id)}
              className={cn(
                'p-3 cursor-pointer transition',
                selId === c.id ? 'bg-amber-50' : 'hover:bg-slate-50'
              )}
            >
              <div className="flex items-start gap-2">
                <Avatar name={c.cliente} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-navy-700 text-xs truncate">{c.cliente}</span>
                    <PriorityChip prioridad={c.prioridad} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{c.tomado}</div>
                  <div className="text-xs text-slate-600 mt-1 line-clamp-2">{c.mensajePreview}</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="p-3 border-t border-slate-200">
          <Button variant="outline" size="sm" className="w-full">
            + Tomar siguiente de cola
          </Button>
        </div>
      </aside>

      {/* COL 2 — CONVERSACIÓN */}
      <section className="flex flex-col overflow-hidden">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar name={sel.cliente} size="md" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-navy-700">{sel.cliente}</span>
                <PriorityChip prioridad={sel.prioridad} withLabel />
              </div>
              <div className="text-xs text-slate-500 font-mono">{sel.wa} · {sel.cedula}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm"><UserCircle2 className="w-3.5 h-3.5" /> Ver cliente</Button>
            <Button variant="emerald" size="sm"><CheckCircle2 className="w-3.5 h-3.5" /> Resolver</Button>
            <Button variant="outline" size="sm"><ArrowUpCircle className="w-3.5 h-3.5" /> Escalar jefe</Button>
            <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
          </div>
        </header>

        <Alert variant="warning" className="rounded-none border-b border-l-0 border-amber-200">
          Chat venía del copilot IA — intentó responder <strong>3 veces</strong>. Razón escalación: <strong>tono agresivo + mención denuncia</strong>.
        </Alert>

        <ChatContainer className="flex-1 overflow-y-auto">
          <div className="text-center text-[10px] text-slate-500 py-1">Historial IA — hoy 14:00</div>
          <ChatBubble from="in" time="14:00">Hola, necesito saber cuánto debo pagar</ChatBubble>
          <ChatBubble from="out" time="14:00 ✓✓" tag={<Chip variant="emerald" className="text-[9px]"><Bot className="w-2.5 h-2.5 mr-0.5 inline" /> IA</Chip>}>
            Hola Pablo. Tu saldo vencido es $340.75 en el crédito #2050124…
          </ChatBubble>
          <ChatBubble from="in" time="14:12">no puede ser, yo ya pagué</ChatBubble>
          <ChatBubble from="out" time="14:12 ✓✓" tag={<Chip variant="emerald" className="text-[9px]"><Bot className="w-2.5 h-2.5 mr-0.5 inline" /> IA</Chip>}>
            En nuestro sistema aparece impago. ¿Me podés mandar foto del comprobante?
          </ChatBubble>
          <ChatBubble from="in" time="14:16">no voy a mandar nada, mandame el comprobante vos</ChatBubble>
          <div className="text-center text-[10px] text-rose-600 py-1 font-semibold">⚠ Escalado a humano · 14:20</div>
          <ChatBubble from="in" time="14:22" highlighted>
            Si no me solucionan esto hoy voy a hacer una denuncia pública. Yo pagué y no voy a aceptar que me cobren otra vez. Esto es un abuso.
          </ChatBubble>
          <div className="text-center text-[10px] text-emerald-600 py-1 font-semibold">✓ Vos tomaste el chat · hace 2 min</div>
        </ChatContainer>

        <div className="border-t border-slate-200">
          <div className="flex items-center gap-1 px-3 py-2 overflow-x-auto border-b border-slate-100">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-2 shrink-0">Plantillas:</span>
            {plantillas.map(p => (
              <button
                key={p}
                onClick={() => setDraft(d => d ? `${d}\n${p}` : p)}
                className="shrink-0 px-2.5 py-1 rounded-full bg-slate-100 text-xs text-slate-700 hover:bg-amber-100 hover:text-amber-700 transition"
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-start gap-2 p-3">
            <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 shrink-0">
              <Paperclip className="w-4 h-4" />
            </button>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={`Escribí tu respuesta a ${sel.cliente.split(' ')[0]}…`}
              rows={2}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 resize-none"
            />
            <Button variant="amber" size="md" disabled={!draft} className="shrink-0">
              <Send className="w-4 h-4" /> Enviar
            </Button>
          </div>
        </div>
      </section>

      {/* COL 3 — CONTEXTO */}
      <aside className="border-l border-slate-200 overflow-y-auto">
        <section className="p-4 bg-slate-50 border-b border-slate-200">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Datos del cliente
          </h3>
          <dl className="space-y-1.5 text-xs">
            <Item label="Cédula" value={<span className="font-mono">{sel.cedula}</span>} />
            <Item label="Plaza" value={sel.plaza} />
            <Item label="Saldo vencido" value={<span className="font-mono text-rose-600 font-semibold">{fmtMoney(sel.saldoVencido)}</span>} />
            <Item label="Días mora" value={<span className="font-mono text-rose-600 font-semibold">{sel.diasMora} días</span>} />
            <Item label="Crédito" value={<span className="font-mono">#{sel.idCredito}</span>} />
            {sel.compromisosIncumplidos > 0 && (
              <Item label="Compromisos" value={<Chip variant="rose">{sel.compromisosIncumplidos} incumplidos</Chip>} />
            )}
          </dl>
        </section>

        <section className="p-4 border-b border-slate-200">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Acciones
          </h3>
          <div className="space-y-2">
            <Button variant="emerald" size="sm" className="w-full justify-start"><DollarSign className="w-3.5 h-3.5" /> Registrar compromiso</Button>
            <Button variant="outline" size="sm" className="w-full justify-start"><Search className="w-3.5 h-3.5" /> Verificar pago en banco</Button>
            <Button variant="outline" size="sm" className="w-full justify-start"><FileText className="w-3.5 h-3.5" /> Ofrecer plan de pago</Button>
            <Button variant="outline" size="sm" className="w-full justify-start"><Phone className="w-3.5 h-3.5" /> Programar llamada</Button>
            <Button variant="outline" size="sm" className="w-full justify-start"><ArrowUpCircle className="w-3.5 h-3.5" /> Escalar a supervisor</Button>
          </div>
        </section>

        <section className="p-4 border-b border-slate-200">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Historial
          </h3>
          <ul className="space-y-2 text-xs">
            <TimelineEntry color="rose" fecha="Hoy 14:22" texto="Escalado a humano (tono agresivo)" />
            <TimelineEntry color="emerald" fecha="Hoy 14:03" texto="IA respondió saldo" />
            <TimelineEntry color="amber" fecha="Hace 2 días" texto="Llamada voz · no contestó" />
            <TimelineEntry color="rose" fecha="Hace 15 días" texto="Compromiso incumplido ($170)" />
            <TimelineEntry color="emerald" fecha="Hace 30 días" texto="Último pago registrado" />
          </ul>
        </section>

        <section className="p-4">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Notas internas</h3>
          <Textarea
            value={nota}
            onChange={e => setNota(e.target.value)}
            placeholder="Agregá notas que verán otros agentes…"
            rows={3}
            className="text-xs"
          />
        </section>
      </aside>
    </div>
  )
}

function Item({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-navy-700 font-medium">{value}</dd>
    </div>
  )
}

function TimelineEntry({ color, fecha, texto }: { color: 'rose' | 'emerald' | 'amber'; fecha: string; texto: string }) {
  const dot = { rose: 'bg-rose-400', emerald: 'bg-emerald-400', amber: 'bg-amber-400' }
  return (
    <li className="flex items-start gap-2">
      <span className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0', dot[color])} />
      <div className="flex-1 min-w-0">
        <div className="text-slate-700">{texto}</div>
        <div className="text-[10px] text-slate-400">{fecha}</div>
      </div>
    </li>
  )
}
